import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import CustomIcon from '@/components/ui/CustomIcon';
import IconButton from '@/components/ui/IconButton';
import classNames from 'classnames';
import styles from './ReportDialog.module.scss';

export const REPORT_DIALOG_ID = 'report-dialog';

interface ReportDialogProps {
  htmlContent: string;
  isOpen: boolean;
  onClose: () => void;
}

const ReportDialog = ({
  htmlContent,
  isOpen,
  onClose,
}: ReportDialogProps): React.ReactElement | null => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [isClosing, setIsClosing] = useState(false);

  const handleClose = (): void => {
    setIsClosing(true);
  };

  const handleAnimationEnd = (): void => {
    if (isClosing) {
      onClose();
      setIsClosing(false);
    }
  };

  const handleExportToPdf = (): void => {
    const printIframe = document.createElement('iframe');
    printIframe.style.position = 'absolute';
    printIframe.style.top = '-9999px';
    printIframe.style.left = '-9999px';
    printIframe.style.width = '0';
    printIframe.style.height = '0';
    printIframe.srcdoc = htmlContent;

    const printStyles = `
            @media print {
                body {
                    margin: 0;
                    padding: 0;
                }
                @page {
                    size: A4;
                    margin: 1cm;
                }
            }
        `;

    printIframe.onload = (): void => {
      try {
        const styleEl = printIframe.contentDocument?.createElement('style');
        if (styleEl && printIframe.contentDocument) {
          styleEl.textContent = printStyles;
          printIframe.contentDocument.head.appendChild(styleEl);

          setTimeout(() => {
            const removeIframe = (): void => {
              document.body.removeChild(printIframe);
            };
            if (printIframe.contentWindow) {
              printIframe.contentWindow.addEventListener('afterprint', removeIframe);
            }

            printIframe.contentWindow?.focus();
            printIframe.contentWindow?.print();

            setTimeout(removeIframe, 60000);
          }, 2000);
        }
      } catch (error) {
        console.error('Error printing:', error);
        document.body.removeChild(printIframe);
      }
    };

    // Add the iframe to the document
    document.body.appendChild(printIframe);
  };

  const handleDismissalAttempt = (target: EventTarget): void => {
    const content = contentRef.current;
    const hasBeenClickedOutsideContentBounds =
      (content && !content.contains(target as Node)) ?? false;
    if (hasBeenClickedOutsideContentBounds) {
      handleClose();
    }
  };

  if (!isOpen) {
    return null;
  }

  return createPortal(
    <div
      className={classNames(styles.overlay, {
        [styles['is-closing']]: isClosing,
      })}
      onClick={event => handleDismissalAttempt(event.target)}
      tabIndex={0}
      role="button"
      onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleDismissalAttempt(event.target);
        }
      }}
      onAnimationEnd={handleAnimationEnd}
    >
      <div
        className={classNames(styles.content, {
          [styles['is-closing']]: isClosing,
        })}
        ref={contentRef}
      >
        <div className={styles.header}>
          <h3 className={styles['header-title']}>Reporte</h3>
          <div className={styles.headerButtons}>
            <button onClick={handleExportToPdf} className={styles.exportButton}>
              <CustomIcon name="export" width={25} height={25} />
              <span className={styles['export-label']}>Exportar PDF</span>
            </button>
            <IconButton iconName="close" onClick={handleClose}>
              <CustomIcon name="close" width={25} height={25} />
            </IconButton>
          </div>
        </div>
        <div className={styles.reportContainer}>
          <iframe
            ref={iframeRef}
            srcDoc={htmlContent}
            className={styles.reportIframe}
            title="Report Content"
            sandbox="allow-scripts allow-modals"
          />
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ReportDialog;
